import { useState } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtAUD } from "@/lib/finance";

export function TransactionsTable({ txns, sources, expenseCats, incomeCats, onUpdate }) {
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sorted = [...txns].sort((a, b) => {
    let av, bv;
    if (sortKey === "amount") { av = a.amount; bv = b.amount; }
    else { av = a[sortKey]; bv = b[sortKey]; }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  return (
    <div className="rounded-sm border border-border bg-card" data-testid="transactions-table">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-medium tracking-tight">Transactions</h3>
        <span className="tabular text-xs text-muted-foreground">{txns.length} rows</span>
      </div>
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-card">
            <tr className="border-b border-border text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">
                <button className="inline-flex items-center gap-1 hover:text-white" onClick={() => toggleSort("date")} data-testid="sort-date">
                  Date <SortIcon col="date" />
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium">Description</th>
              <th className="px-3 py-2 text-right font-medium">
                <button className="inline-flex items-center gap-1 hover:text-white" onClick={() => toggleSort("amount")} data-testid="sort-amount">
                  Amount <SortIcon col="amount" />
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium w-[210px]">Category</th>
              <th className="px-3 py-2 text-left font-medium w-[150px]">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((t) => {
              const cats = t.amount >= 0 ? incomeCats : expenseCats;
              return (
                <tr key={t.id} className="hover:bg-white/[0.02]" data-testid={`txn-row-${t.id}`}>
                  <td className="whitespace-nowrap px-3 py-2 tabular text-muted-foreground">
                    {format(parseISO(t.date), "dd/MM/yy")}
                  </td>
                  <td className="px-3 py-2 max-w-[340px] truncate text-neutral-200" title={t.description}>
                    {t.description || "—"}
                  </td>
                  <td className={`px-3 py-2 text-right tabular font-medium ${t.amount >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {t.amount >= 0 ? "+" : "−"}{fmtAUD(Math.abs(t.amount))}
                  </td>
                  <td className="px-3 py-2">
                    <Select value={t.category} onValueChange={(v) => onUpdate(t.id, { category: v })}>
                      <SelectTrigger className="h-7 rounded-sm border-border bg-secondary text-xs" data-testid={`category-select-${t.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {cats.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Select value={t.source} onValueChange={(v) => onUpdate(t.id, { source: v })}>
                      <SelectTrigger className="h-7 rounded-sm border-border bg-secondary text-xs" data-testid={`source-select-${t.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sources.filter((s) => s !== "all").map((s) => (
                          <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
